import React from 'react';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import { Brain } from 'lucide-react';
export default function Neurodevelopmental() {
  return <main className="min-h-screen bg-white" data-id="element-1154">
      <Helmet data-id="element-1155">
        <title data-id="element-1156">
          ADHD & Autism Medico-Legal Assessments | Medico-Legal Psychiatry
        </title>
        <meta name="description" content="Expert ADHD & Autism assessments for legal proceedings. Comprehensive evaluations of how neurodevelopmental conditions impact legal capacity and behavior." data-id="element-1157" />
        <meta name="keywords" content="ADHD & Autism Medico-Legal Assessments, ADHD Assessment, Autism Evaluation, Neurodevelopmental Disorders, Expert Witness, Legal Capacity, ADHD medico-legal reports for court cases UK" data-id="element-1158" />
        <script type="application/ld+json" data-id="element-1159">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "ADHD & Autism Medico-Legal Assessments",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Neurodevelopmental Assessment",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert ADHD & Autism Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1160">
        <div className="container mx-auto px-4" data-id="element-1161">
          <div className="flex items-center justify-center mb-4" data-id="element-1162">
            <Brain className="h-12 w-12 mr-4" data-id="element-1163" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-1164">
              ADHD & Autism Medico-Legal Assessments
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1165">
            Specialized evaluations of ADHD, Autism, and their legal
            implications
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1166">
        <div className="container mx-auto px-4" data-id="element-1167">
          <div className="max-w-3xl mx-auto" data-id="element-1168">
            <div className="prose max-w-none" data-id="element-1169">
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1170">
                Expert Neurodevelopmental Evaluations
              </h2>
              <p className="text-slate-dark mb-6" data-id="element-1171">
                Dr. Syed provides comprehensive assessments of
                neurodevelopmental conditions, with a particular focus on
                Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism
                Spectrum Disorder (ASD). These specialized evaluations are
                crucial for understanding how these conditions may impact an
                individual's legal context, decision-making capacity, or
                behavior.
              </p>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1172">
                Our ADHD & Autism Assessment Services Include:
              </h3>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1173">
                <li className="mb-2" data-id="element-1174">
                  <strong data-id="element-1175">ADHD Evaluations:</strong> Comprehensive assessments
                  of attention, impulsivity, and hyperactivity symptoms, with
                  consideration of how these may affect legal capacity,
                  culpability, or compliance with legal requirements.
                </li>
                <li className="mb-2" data-id="element-1176">
                  <strong data-id="element-1177">Autism Spectrum Assessments:</strong> Detailed
                  evaluations of social communication, interaction patterns, and
                  restricted/repetitive behaviors, with analysis of how these
                  features may influence legal understanding and behavior.
                </li>
                <li className="mb-2" data-id="element-1178">
                  <strong data-id="element-1179">Impact on Decision-Making Capacity:</strong> Specific
                  assessments of how neurodevelopmental conditions may affect an
                  individual's capacity to make decisions in various legal
                  contexts.
                </li>
                <li className="mb-2" data-id="element-1180">
                  <strong data-id="element-1181">Behavioral Analysis:</strong> Expert interpretation of
                  how neurodevelopmental features may have influenced past
                  behaviors relevant to legal proceedings.
                </li>
                <li data-id="element-1182">
                  <strong data-id="element-1183">Reasonable Adjustments:</strong> Recommendations for
                  appropriate accommodations in legal settings to ensure fair
                  treatment of individuals with neurodevelopmental conditions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1184">
                Our Methodology
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1185">
                Dr. Syed's neurodevelopmental assessments are characterized by:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1186">
                <li className="mb-2" data-id="element-1187">
                  <strong data-id="element-1188">Comprehensive Approach:</strong> Detailed clinical
                  interviews, standardized assessments, and consideration of
                  developmental history.
                </li>
                <li className="mb-2" data-id="element-1189">
                  <strong data-id="element-1190">Contextual Understanding:</strong> Careful analysis of
                  how neurodevelopmental features manifest in specific
                  situations relevant to the legal context.
                </li>
                <li className="mb-2" data-id="element-1191">
                  <strong data-id="element-1192">Differential Diagnosis:</strong> Consideration of
                  other conditions that may mimic or co-occur with ADHD or ASD.
                </li>
                <li data-id="element-1193">
                  <strong data-id="element-1194">Legal Relevance:</strong> Clear articulation of how
                  identified neurodevelopmental features relate to specific
                  legal questions.
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1195">
                When to Request an ADHD or Autism Assessment
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1196">
                These evaluations are particularly valuable in:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-1197">
                <li className="mb-2" data-id="element-1198">
                  Criminal proceedings where ADHD or autism may have influenced
                  behavior
                </li>
                <li className="mb-2" data-id="element-1199">
                  Capacity assessments where neurodevelopmental conditions may
                  affect decision-making
                </li>
                <li className="mb-2" data-id="element-1200">
                  Family court proceedings involving parents or children with
                  neurodevelopmental conditions
                </li>
                <li className="mb-2" data-id="element-1201">
                  Employment tribunal cases related to disability discrimination
                </li>
                <li data-id="element-1202">
                  Immigration cases where vulnerability due to
                  neurodevelopmental conditions is relevant
                </li>
              </ul>
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1203">
                Expert Court Testimony
              </h3>
              <p className="text-slate-dark mb-6" data-id="element-1204">
                Dr. Syed provides clear, accessible expert testimony on
                neurodevelopmental conditions, helping courts and tribunals
                understand the complex ways these conditions may influence
                behavior, capacity, and legal responsibility.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 shadow-md mb-8" data-id="element-1205">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1206">
                  Ready to Instruct Dr. Syed?
                </h3>
                <p className="text-slate-dark mb-4" data-id="element-1207">
                  For neurodevelopmental assessments that provide crucial
                  insights for legal proceedings, contact us today to discuss
                  your case requirements.
                </p>
                <Button variant="primary" href="/contact" data-id="element-1208">
                  Book an ADHD or Autism Assessment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}